import { FleetPhotosDialog, FleetExpensesDialog } from "./RentalFleetExtras";
import { Image as ImageIcon } from "lucide-react";
import { RentalFleetImport } from "./RentalFleetImport";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Bike, Car, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api } from "../../lib/api";

export const STATUS = {
  available: { label: "متاحة", cls: "bg-emerald-100 text-emerald-700" },
  rented: { label: "مؤجرة", cls: "bg-blue-100 text-blue-700" },
  maintenance: { label: "صيانة", cls: "bg-amber-100 text-amber-700" },
};
const NUMS = [["daily_rate", "السعر اليومي"], ["weekly_rate", "السعر الأسبوعي"], ["monthly_rate", "السعر الشهري"],
  ["deposit", "مبلغ التأمين"], ["km_limit_day", "حد الكيلومترات اليومي"], ["extra_km_rate", "سعر الكيلو الزائد"], ["fuel_full_cost", "تكلفة تعبئة الخزان كاملاً"]];
const EMPTY = { vehicle_id: "", identifier: "", vtype: "سيارة", branch: "", model: "", daily_rate: "", weekly_rate: "", monthly_rate: "", deposit: "", km_limit_day: "", extra_km_rate: "", fuel_full_cost: "", maintenance: false, notes: "" };

const VehiclePicker = ({ options, value, onChange }) => {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase().replace(/\s+/g, "");
  const list = options.filter((o) => !s || `${o.identifier} ${o.type} ${o.branch}`.toLowerCase().replace(/\s+/g, "").includes(s));
  const sel = new Set(value);
  const toggle = (id) => onChange(sel.has(id) ? value.filter((x) => x !== id) : [...value, id]);
  const allOn = list.length > 0 && list.every((o) => sel.has(o.id));
  const toggleAll = () => onChange(allOn ? value.filter((x) => !list.some((o) => o.id === x)) : [...new Set([...value, ...list.map((o) => o.id)])]);
  return (
    <div className="rounded-lg border border-slate-200" data-testid="rental-fleet-vehicle-picker">
      <div className="relative border-b border-slate-100"><Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`ابحث برقم اللوحة أو النوع أو الفرع (${options.length})`} className="h-10 w-full bg-transparent pr-9 pl-3 text-sm outline-none" data-testid="rental-fleet-vehicle-search" /></div>
      {list.length > 0 && (
        <label className="flex cursor-pointer items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="rental-fleet-vehicle-all">
          <span className="flex items-center gap-2"><input type="checkbox" checked={allOn} onChange={toggleAll} className="h-4 w-4 accent-blue-600" /> تحديد الكل{q ? " (النتائج)" : ""}</span>
          <span className="text-blue-700" data-testid="rental-fleet-vehicle-count">{value.length ? `محدد ${value.length}` : ""}</span>
        </label>
      )}
      <div className="max-h-56 overflow-y-auto">
        {!list.length ? <p className="py-4 text-center text-xs text-slate-400">لا توجد مركبة مطابقة</p> : list.map((o) => (
          <label key={o.id} data-testid={`rental-fleet-vehicle-opt-${o.id}`}
            className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-right text-sm hover:bg-blue-50 ${sel.has(o.id) ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-700"}`}>
            <input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} className="h-4 w-4 shrink-0 accent-blue-600" />
            <span><span dir="ltr">{o.identifier || "—"}</span> <span className="text-xs text-slate-400">— {o.type}{o.branch ? ` — ${o.branch}` : ""}</span></span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const FleetDialog = ({ open, onOpenChange, editing, onSaved, preset }) => {
  const [f, setF] = useState(EMPTY);
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ids, setIds] = useState([]);
  const [own, setOwn] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(editing ? { ...EMPTY, ...editing } : { ...EMPTY, vehicle_id: preset?.id || "" });
    setIds([]);
    setOwn(false);
    if (!editing && !preset) api.rentalVehicleOptions().then(setOptions).catch(() => setOptions([]));
  }, [open, editing, preset]);
  const save = async () => {
    const solo = own && !editing && !preset;
    const multi = !editing && !preset && !solo;
    if (solo && !f.identifier.trim()) { toast.warning("اكتب رقم اللوحة"); return; }
    if (!solo && (multi ? !ids.length : (!editing && !f.vehicle_id))) { toast.warning("اختر المركبة"); return; }
    setBusy(true);
    const body = { ...f, ...(solo ? { vehicle_id: "" } : {}), ...Object.fromEntries(NUMS.map(([k]) => [k, Number(f[k]) || 0])) };
    try {
      if (editing) await api.updateRentalFleet(editing.id, body);
      else if (solo || !multi) await api.addRentalFleet(body);
      else {
        let ok = 0, fail = 0;
        for (const id of ids) { try { await api.addRentalFleet({ ...body, vehicle_id: id }); ok += 1; } catch { fail += 1; } }
        if (!ok) throw new Error("x");
        toast.success(`تمت إضافة ${ok} مركبة للتأجير${fail ? ` · تعذّر ${fail}` : ""}`); onOpenChange(false); onSaved(); return;
      }
      toast.success("تم الحفظ"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto overflow-x-hidden" dir="rtl" data-testid="rental-fleet-dialog">
        <DialogHeader><DialogTitle>{editing ? `تعديل ${editing.identifier}` : preset ? `تعيين ${preset.identifier || ""} كمركبة إيجار` : "إضافة مركبات للتأجير"}</DialogTitle>
          <DialogDescription>{own || editing?.standalone ? "مركبة خاصة بالتأجير — لا تظهر في الأسطول أو العهد أو أي قسم آخر." : "اختر مركبة من قسم الأسطول وحدّد أسعار التأجير."}</DialogDescription></DialogHeader>
        {!editing && !preset && (
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" data-testid="rental-fleet-source">
            {[[false, "من الأسطول"], [true, "مركبة جديدة للتأجير فقط"]].map(([v, l]) => (
              <button key={l} type="button" onClick={() => setOwn(v)} data-testid={`rental-fleet-source-${v ? "own" : "fleet"}`}
                className={`rounded-lg py-2 text-sm font-bold transition-colors ${own === v ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>))}
          </div>
        )}
        {own && !editing && !preset && <RentalFleetImport prices={Object.fromEntries(NUMS.map(([k]) => [k, f[k]]))} onDone={() => { onOpenChange(false); onSaved(); }} />}
        {((own && !editing && !preset) || editing?.standalone) && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">رقم اللوحة *</Label><Input data-testid="rental-fleet-plate" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} placeholder="أ ب ج 1234" /></div>
            <div className="space-y-1.5"><Label className="text-xs">النوع</Label>
              <select data-testid="rental-fleet-vtype" value={f.vtype} onChange={(e) => setF({ ...f, vtype: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {["سيارة", "دراجة نارية", "فان", "شاحنة", "باص"].map((x) => <option key={x}>{x}</option>)}</select></div>
            <div className="space-y-1.5"><Label className="text-xs">الماركة / الموديل / السنة</Label><Input data-testid="rental-fleet-model" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="تويوتا يارس 2024 أبيض" /></div>
            <div className="space-y-1.5"><Label className="text-xs">الفرع</Label><Input data-testid="rental-fleet-branch" value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} /></div>
          </div>
        )}
        {!editing && !preset && !own && (
          <div className="space-y-1.5"><Label>المركبة</Label>
            <VehiclePicker options={options} value={ids} onChange={setIds} />
            {ids.length > 1 && <p className="text-[11px] text-slate-500">نفس الأسعار والإعدادات تنطبق على كل المركبات المحددة، وتقدر تعدّل كل مركبة بعدين لحالها.</p>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {NUMS.map(([k, label]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{label}</Label>
              <Input data-testid={`rental-fleet-${k}`} type="number" dir="ltr" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="rental-fleet-maintenance">
          <input type="checkbox" checked={!!f.maintenance} onChange={(e) => setF({ ...f, maintenance: e.target.checked })} className="h-4 w-4 accent-amber-500" /> المركبة في الصيانة (غير متاحة للتأجير)
        </label>
        <Input placeholder="ملاحظات" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="rental-fleet-notes" />
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="rental-fleet-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ{!editing && !preset && ids.length > 1 ? ` (${ids.length})` : ""}</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BulkDialog = ({ open, onOpenChange, ids, onSaved }) => {
  const [v, setV] = useState({});
  const [mt, setMt] = useState("keep");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setV({}); setMt("keep"); } }, [open]);
  const save = async () => {
    const values = Object.fromEntries(Object.entries(v).filter(([, x]) => x !== ""));
    if (mt !== "keep") values.maintenance = mt === "on";
    if (!Object.keys(values).length) { toast.warning("اكتب قيمة وحدة على الأقل"); return; }
    setBusy(true);
    try { const r = await api.rentalFleetBulk(ids, values); toast.success(`تم تعديل ${r.matched} مركبة`); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="rental-bulk-dialog">
        <DialogHeader><DialogTitle>تعديل أسعار {ids.length} مركبة</DialogTitle>
          <DialogDescription>اكتب القيم اللي تبي تغيّرها بس، والخانات الفاضية تبقى على قيمتها الحالية لكل مركبة.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {NUMS.map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label>
              <Input data-testid={`rental-bulk-${k}`} type="number" dir="ltr" placeholder="بدون تغيير" value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></div>
          ))}
          <div className="space-y-1.5"><Label className="text-xs">الصيانة</Label>
            <select value={mt} onChange={(e) => setMt(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="rental-bulk-maintenance">
              <option value="keep">بدون تغيير</option><option value="on">في الصيانة</option><option value="off">متاحة للتأجير</option>
            </select></div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="rental-bulk-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ للكل</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FLEET_ST = [["", "كل الحالات"], ["available", "متاحة"], ["rented", "مؤجرة"], ["maintenance", "صيانة"]];

export const RentalFleetTab = ({ canEdit, onChanged, preset }) => {
  const [st, setSt] = useState(preset || "");
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [photoOf, setPhotoOf] = useState(null);
  const [expOf, setExpOf] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [bulk, setBulk] = useState(false);
  const load = useCallback(() => api.rentalFleet().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const after = () => { load(); onChanged(); };
  const toggleAuth = async (c) => {
    try { await api.rentalSetAuthorization(c.id, !c.authorized); toast.success(c.authorized ? "تم إلغاء التفويض" : "تم تأكيد التفويض"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحديث"); }
  };
  const del = async (r) => {
    if (!window.confirm(`إزالة ${r.identifier} من أسطول التأجير؟`)) return;
    try { await api.deleteRentalFleet(r.id); toast.success("تمت الإزالة"); after(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  if (!rows) return <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;
  const ss = q.trim().toLowerCase().replace(/\s+/g, "");
  const shown = rows.filter((r) => (!st || r.status === st) && (!ss || `${r.identifier} ${r.vtype} ${r.branch} ${r.active_contract?.renter_name || ""}`.toLowerCase().replace(/\s+/g, "").includes(ss)));
  const allPicked = shown.length > 0 && shown.every((r) => picked.includes(r.id));
  const pickAll = () => setPicked(allPicked ? picked.filter((id) => !shown.some((r) => r.id === id)) : [...new Set([...picked, ...shown.map((r) => r.id)])]);
  const pick = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && <Button data-testid="rental-fleet-add" onClick={() => setDlg({ open: true, editing: null })} className="bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> إضافة مركبة للتأجير</Button>}
        {canEdit && picked.length > 0 && <>
          <Button variant="outline" data-testid="rental-bulk-btn" onClick={() => setBulk(true)} className="gap-1.5 border-blue-300 text-blue-700"><Pencil className="h-4 w-4" /> تعديل أسعار المحدد ({picked.length})</Button>
          <button onClick={() => setPicked([])} className="text-xs text-slate-400 hover:text-slate-600" data-testid="rental-bulk-clear">إلغاء التحديد</button>
        </>}
        {rows.length > 0 && <select data-testid="rental-fleet-status-filter" value={st} onChange={(e) => setSt(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm">
          {FLEET_ST.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>}
        {rows.length > 0 && <div className="relative min-w-[200px] flex-1 sm:max-w-xs"><Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث برقم اللوحة أو المستأجر" className="pr-9" data-testid="rental-fleet-search" /></div>}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-400" data-testid="rental-fleet-empty">لا توجد مركبات تأجير بعد</div>
      ) : (
        <>
        {canEdit && <label className="flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-slate-600" data-testid="rental-fleet-pick-all"><input type="checkbox" checked={allPicked} onChange={pickAll} className="h-4 w-4 accent-blue-600" /> تحديد الكل{q ? " (النتائج)" : ""}</label>}
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {shown.map((r) => {
            const Icon = r.kind === "moto" ? Bike : Car;
            return (
              <div key={r.id} data-testid={`rental-fleet-card-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">{canEdit && <input type="checkbox" checked={picked.includes(r.id)} onChange={() => pick(r.id)} className="h-4 w-4 accent-blue-600" data-testid={`rental-fleet-pick-${r.id}`} />}<div className="h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center"><Icon className="h-5 w-5 text-slate-600" /></div>
                    <div><div className="font-bold text-slate-800">{r.identifier || "—"}</div><div className="text-xs text-slate-400">{r.vtype}{r.model ? ` · ${r.model}` : ""}{r.branch ? ` · ${r.branch}` : ""}{r.standalone && <span className="ms-1 rounded bg-violet-50 px-1.5 text-[10px] font-bold text-violet-700" data-testid={`rental-fleet-solo-${r.id}`}>تأجير فقط</span>}</div></div></div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS[r.status]?.cls}`} data-testid={`rental-fleet-status-${r.id}`}>{STATUS[r.status]?.label}{r.manual_rented && " · بدون عقد"}</span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center text-xs">
                  {[["يومي", r.daily_rate], ["أسبوعي", r.weekly_rate], ["شهري", r.monthly_rate]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-slate-50 py-1.5"><div className="text-slate-400">{l}</div><div className="font-bold text-slate-700 tabular-nums">{v || "—"}</div></div>
                  ))}
                </div>
                {r.active_contract && (
                  <div className="flex items-center justify-between gap-2 text-xs text-blue-700 bg-blue-50 rounded-lg px-2 py-1">
                    <span>عقد {r.active_contract.number} · {r.active_contract.renter_name} · حتى {r.active_contract.end_date} {r.active_contract.end_time || ""}</span>
                    <button type="button" disabled={!canEdit} data-testid={`rental-fleet-auth-${r.id}`} onClick={() => toggleAuth(r.active_contract)}
                      className={`shrink-0 rounded-full px-2 py-0.5 font-semibold ${r.active_contract.authorized ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.active_contract.authorized ? "مفوّض" : "غير مفوّض"}</button>
                  </div>
                )}
                {canEdit && (
                  <div className="flex justify-end gap-1">
                    <button data-testid={`rental-fleet-photos-${r.id}`} onClick={() => setPhotoOf(r)} className="flex items-center gap-1 p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 text-xs"><ImageIcon className="h-4 w-4" />{(r.photos || []).length || ""}</button>
                    <button data-testid={`rental-fleet-expenses-${r.id}`} onClick={() => setExpOf(r)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 text-xs font-bold">مصروف</button>
                    <button data-testid={`rental-fleet-edit-${r.id}`} onClick={() => setDlg({ open: true, editing: r })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`rental-fleet-delete-${r.id}`} onClick={() => del(r)} className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
      {photoOf && <FleetPhotosDialog fleet={photoOf} onClose={() => setPhotoOf(null)} onSaved={after} />}
      {expOf && <FleetExpensesDialog fleet={expOf} onClose={() => setExpOf(null)} onSaved={after} />}
      <BulkDialog open={bulk} onOpenChange={setBulk} ids={picked} onSaved={() => { setPicked([]); after(); }} />
      <FleetDialog open={dlg.open} editing={dlg.editing} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={after} />
    </div>
  );
};
